import * as yup from "yup";

export const PASSWORD_POLICY_VIOLATION = "PASSWORD_POLICY_VIOLATION";

export const PASSWORD_SPECIAL_CHARACTERS =
  "!@#$%^&*()_-+={}[]|:;\"'<>,.?/~`";
export const PASSWORD_SPECIAL_CHARACTERS_DISPLAY =
  Array.from(PASSWORD_SPECIAL_CHARACTERS).join(" ");

export const PASSWORD_POLICY_MESSAGE =
  `Password must be at least 8 characters and include an ASCII lowercase letter, an ASCII uppercase letter, an ASCII digit, and one allowed special character. Allowed special characters (space-separated): ${PASSWORD_SPECIAL_CHARACTERS_DISPLAY}. Maximum: 72 UTF-8 bytes.`;

export const PASSWORD_POLICY_HELP =
  `Use at least 8 characters with an ASCII lowercase letter, an ASCII uppercase letter, an ASCII digit, and one allowed special character. Allowed special characters (space-separated): ${PASSWORD_SPECIAL_CHARACTERS_DISPLAY}. Maximum: 72 UTF-8 bytes.`;

const hasAllowedSpecialCharacter = (value) =>
  typeof value === "string" &&
  Array.from(value).some((character) =>
    PASSWORD_SPECIAL_CHARACTERS.includes(character)
  );

export const utf8ByteLength = (value) => {
  let bytes = 0;

  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);

    if (codeUnit < 0x80) {
      bytes += 1;
    } else if (codeUnit < 0x800) {
      bytes += 2;
    } else if (
      codeUnit >= 0xd800 &&
      codeUnit <= 0xdbff &&
      index + 1 < value.length &&
      value.charCodeAt(index + 1) >= 0xdc00 &&
      value.charCodeAt(index + 1) <= 0xdfff
    ) {
      bytes += 4;
      index += 1;
    } else {
      // Lone surrogates are encoded as the three-byte replacement character.
      bytes += 3;
    }
  }

  return bytes;
};

export const unicodeCharacterLength = (value) => Array.from(value).length;

export const passwordValidationSchema = yup
  .string()
  .strict(true)
  .typeError("Password must be a string")
  .required("Password is required")
  .test(
    "min-unicode-characters",
    PASSWORD_POLICY_MESSAGE,
    (value) => !value || unicodeCharacterLength(value) >= 8
  )
  .matches(/[a-z]/, PASSWORD_POLICY_MESSAGE)
  .matches(/[A-Z]/, PASSWORD_POLICY_MESSAGE)
  .matches(/\d/, PASSWORD_POLICY_MESSAGE)
  .test(
    "allowed-special-character",
    PASSWORD_POLICY_MESSAGE,
    (value) => !value || hasAllowedSpecialCharacter(value)
  )
  .test(
    "max-utf8-bytes",
    PASSWORD_POLICY_MESSAGE,
    (value) => !value || utf8ByteLength(value) <= 72
  );

export const resetPasswordValidationSchema = yup.object({
  password: passwordValidationSchema,
  confirmpassword: yup
    .string()
    .strict(true)
    .typeError("Confirm password must be a string")
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

export const hasCompleteResetParams = (token, email) =>
  typeof token === "string" &&
  token.trim().length > 0 &&
  typeof email === "string" &&
  email.trim().length > 0;

export const validateResetLink = ({
  token,
  email,
  validateToken,
  onValid,
  onInvalid,
}) => {
  if (!hasCompleteResetParams(token, email)) {
    onInvalid();
    return false;
  }

  try {
    validateToken(
      token,
      email,
      (response) => {
        if (response?.data?.status === "success") {
          onValid();
        } else {
          onInvalid();
        }
      },
      onInvalid
    );
  } catch (_error) {
    onInvalid();
  }

  return true;
};

const responseData = (responseOrError) =>
  responseOrError?.response?.data || responseOrError?.data || responseOrError || {};

export const getPasswordResetErrorMessage = (responseOrError) => {
  const data = responseData(responseOrError);
  const code = data.code || data.error_code || data.error?.code;

  if (code === PASSWORD_POLICY_VIOLATION) {
    return PASSWORD_POLICY_MESSAGE;
  }

  if (typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }

  return "Something went wrong. Please try again later.";
};
